import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { featuredDramas } from "@/mocks/dramas";

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const slides = featuredDramas;

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const goTo = (index: number) => {
    setCurrent(index);
  };

  return (
    <section className="relative w-full h-[520px] md:h-[650px] overflow-hidden">
      {slides.map((drama, index) => (
        <div
          key={drama.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === current ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={drama.heroImage}
            alt={drama.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>
      ))}

      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4 md:px-6 max-w-[1400px] mx-auto">
        <div className="w-full max-w-3xl">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-background-50 mb-4 md:mb-6 leading-tight">
            {slides[current]?.title}
          </h1>
          <p className="text-sm md:text-base text-background-50/80 mb-4 md:mb-6 max-w-xl mx-auto leading-relaxed line-clamp-2 md:line-clamp-3">
            {slides[current]?.description}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 mb-6 md:mb-8">
            {slides[current]?.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full text-xs font-medium bg-white/15 text-background-50 backdrop-blur-sm whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
            <span className="text-sm text-background-50/80 whitespace-nowrap">
              <i className="ri-star-fill text-accent-500 mr-1"></i>
              {slides[current]?.rating}
            </span>
            <span className="text-sm text-background-50/60 whitespace-nowrap">
              {slides[current]?.updateInfo}
            </span>
          </div>
          <div className="flex items-center justify-center gap-3">
            <Link
              to={`/article/${slides[current]?.slug}`}
              className="px-6 py-2.5 md:px-8 md:py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm md:text-base hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line mr-2"></i>
              立即观看
            </Link>
            <Link
              to={`/category/${slides[current]?.categorySlug}`}
              className="px-6 py-2.5 md:px-8 md:py-3 rounded-full border border-white/30 text-background-50 font-medium text-sm md:text-base hover:bg-white/10 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              更多推荐
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goTo(index)}
            className={`w-2 h-2 md:w-2.5 md:h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
              index === current
                ? "bg-primary-500 w-6 md:w-8"
                : "bg-white/40 hover:bg-white/60"
            }`}
            aria-label={`切换到第 ${index + 1} 张`}
          />
        ))}
      </div>

      <button
        onClick={() =>
          setCurrent((prev) => (prev - 1 + slides.length) % slides.length)
        }
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/10 text-background-50 hover:bg-white/20 transition-all duration-300 cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/10 text-background-50 hover:bg-white/20 transition-all duration-300 cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>
    </section>
  );
}